import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Button, TextField } from "@mui/material";
import { DataGridPro, GridRowModes, useGridApiRef } from "@mui/x-data-grid-pro";

import DocumentAuditFilterSection from "./DocumentAuditFilterSection";
import DocumentStatusCell from "./DocumentStatusCell";
import UploadDocumentModal from "./UploadDocumentModal";
import { TraceWrapper } from "../../../components/styledComponents";
import { getDocumentAuditData,updateAuditDocuments } from "../../../store/actions/documentAction";
import { useDispatch } from "react-redux";
import { convertYMDToMDY } from "../../../utils/global-functions";
import CustomDatePicker from "../../../components/CustomDatePicker";
import { convertMDYToYMD } from "../../../utils/global-functions";
import PermissionWrapper from "../../Setting/PermissionWrapper";
import { useSelector } from "react-redux";
import { hasPermission } from "../../Setting/permissionHelper";

export default function RequiredDocumentAudit() {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();

  /* ---------------- EDIT TRACKING ---------------- */

  const unsavedChangesRef = useRef({
    unsavedRows: {},
    rowsBeforeChange: {}
  });

  const [rowModesModel, setRowModesModel] = useState({});
  const [editedRowIds, setEditedRowIds] = useState(new Set());
  const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /* ---------------- GRID DATA ---------------- */

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ---------------- FILTER / UI ---------------- */

  const [filters, setFilters] = useState({});
  const [uploadState, setUploadState] = useState(null);
  const [selectedLocationList, setSelectedLocationList] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);

  /* ---------------- PAGINATION ---------------- */

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sortModel, setSortModel] = useState([
    { field: "control", sort: "desc" }
  ]);
  const [rowCount, setRowCount] = useState(0);

  const permissionsData = useSelector(
    (state) => state.rolePermissionReducer.userPermissionData
  );

  /* ---------------- LOAD DATA ---------------- */

  const loadData = async ({
    pageParam = page,
    pageSizeParam = pageSize,
    sortParam = sortModel,
  } = {}) => {
    setLoading(true);
  
    const params = {
      ...filters,
      page: pageParam + 1,
      pageSize: pageSizeParam,
      sortBy: sortParam[0]?.field || "control",
      sortOrder: sortParam[0]?.sort || "desc",
      locationNumber: selectedLocationList
    };
  
    const res = await dispatch(getDocumentAuditData(params));
  
    setRows(res?.rows || []);
    setRowCount(res?.totalRows || 0);
    setEditedRowIds(new Set());
    setLoading(false);
  };

  const handleSortChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPage(0);
  
    loadData({
      pageParam: 0,
      pageSizeParam: pageSize,
      sortParam: newSortModel
    });
  };
  // useEffect(() => {
  //   if (filters?.dateType) {
  //     loadData();
  //   }
  // }, [loadData]);
  // useEffect(() => {
  //   if (filters?.dateType) {
  //     loadData();
  //   }
  // }, [filters, page, pageSize, sortModel, selectedLocationList]);
  useEffect(() => {
    if (filters?.dateType) {
      setPage(0);
  
      loadData({
        pageParam: 0,
        pageSizeParam: pageSize,
        sortParam: sortModel
      });
    }
  }, [filters, selectedLocationList]);

  /* ---------------- UPLOAD ---------------- */

  const handleOpenUpload = (row, docType) => {
    let docTypeDetails = null;

    if (docType === "BOL_POD_POP") {
      const bolDoc = row.documents?.find(d => d.docTypeAbbrevation === "BOL");
      if (bolDoc) {
        docTypeDetails = {
          id: bolDoc.docTypeId,
          code: bolDoc.docTypeCode,
          abbrevation: bolDoc.docTypeAbbrevation,
          BOL_POD_POP: true
        };
      }
    } else {
      const matchedDoc = row.documents?.find(
        d => d.docTypeAbbrevation === docType
      );
      if (matchedDoc) {
        docTypeDetails = {
          id: matchedDoc.docTypeId,
          code: matchedDoc.docTypeCode,
          abbrevation: matchedDoc.docTypeAbbrevation
        };
      }
    }

    setUploadState({ row, docTypeDetails });
  };
  console.log("............................UploadState",uploadState)

  /* ---------------- PROCESS UPDATE ---------------- */

  const processRowUpdate = (newRow, oldRow) => {
    const rowId = newRow.id;

    unsavedChangesRef.current.unsavedRows[rowId] = newRow;

    if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
      unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
    }

    setEditedRowIds(prev => new Set(prev).add(rowId));
    setHasUnsavedRows(true);

    return newRow;
  };

  /* ---------------- SAVE / CANCEL ---------------- */

  const handleSave = async () => {
    setIsSaving(true);

    const payload = Object.values(
      unsavedChangesRef.current.unsavedRows
    ).map(row => ({
      updateRequestFrom: "audit",
      controlNumber: row.control || "",
      billingReference: row.reference || "",
      unitNumber: row.unit || "",
      billingDate: convertMDYToYMD(row.billingDate),
      returnDate: convertMDYToYMD(row.returnDate),
      returnTime: row.returnTime || "00:00",
      exceptionNote: row.exceptionNote || ""
    }));
    console.log("payload",payload)

    await updateAuditDocuments(selectedLocationList[0],payload);

    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {}
    };

    setRowModesModel({});
    setHasUnsavedRows(false);
    setIsSaving(false);
    setEditedRowIds(new Set());
    loadData();
  };

  const handleCancel = () => {
    const originalRows = unsavedChangesRef.current.rowsBeforeChange;

    setRows(prev =>
      prev.map(row => originalRows[row.id] || row)
    );

    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {}
    };

    setRowModesModel({});
    setEditedRowIds(new Set());
    setHasUnsavedRows(false);
    
  };

  /* ---------------- NEXT ROW (TRACE BEHAVIOR) ---------------- */

  const editNextRow = (currentRowId) => {
    const index = rows.findIndex(r => r.id === currentRowId);
    const nextRow = rows[index + 1];
    if (!nextRow) return;

    setRowModesModel({
      [currentRowId]: { mode: GridRowModes.View },
      [nextRow.id]: { mode: GridRowModes.Edit }
    });

    setEditedRowIds(prev => new Set(prev).add(currentRowId));
  };

  /* ---------------- COLUMNS ---------------- */
  const canEditDocuments = hasPermission(
    permissionsData,
    "documents",
    "READ_WRITE"
  );
  const columns = [
        { field: "locationNumber", headerName: "Loc#", width: 120 },
        {
          field: 'control',
          headerName: 'Control #',
          minWidth: 100,
          flex: 1,
          renderCell: (params) => {
              return (
                  <a href={`/orders/${params.row.locationNumber}/${params.row.control}`} target='_blank' >{params.row.control}</a>
              )
          }
      },

        { field: "customerName", headerName: "Customer", width: 220 },
        { field: "billToName", headerName: "Bill To Code", width: 220 },
        // { field: "billingDate", headerName: "Billing Date", width: 130 },
        {
          field: "billingDate",
          headerName: "Billing Date",
          width: 130,
          editable: canEditDocuments,
        
          // VIEW MODE
          renderCell: (params) =>
            params.value ? convertYMDToMDY(params.value) : "",
        
          // EDIT MODE
          renderEditCell: (params) => (
            <CustomDatePicker
              size="small"
              className="width_100"
              variant="standard"
              value={params.value || ""}
              hasFocus={params.hasFocus}
              isArrowKeyDisabled={true}
              onValueChange={(selectedVal) => {
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: selectedVal // already YYYY-MM-DD
                });
              }}
            />
          )
        },
        // {
        //   field: "reference",
        //   headerName: "Reference",
        //   width: 160,
        //   cellClassName: (p) => (!p.value ? "ref-missing" : "")
        // },
        {
          field: "reference",
          headerName: "Reference",
          width: 160,
          editable: canEditDocuments,
          renderEditCell: (params) => (
            <TextField
              variant="standard"
              fullWidth
              value={params.value || ""}
              onChange={(e) =>
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: e.target.value
                })
              }
            />
          )
        },
        // { field: "unit", headerName: "Unit", width: 100 },
        {
          field: "unit",
          headerName: "Unit",
          width: 100,
          editable: canEditDocuments,
          renderEditCell: (params) => (
            <TextField
              variant="standard"
              fullWidth
              value={params.value || ""}
              onChange={(e) =>
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: e.target.value
                })
              }
            />
          )
        },
        // { field: "returnDate", headerName: "Return Date", width: 130 },
        {
          field: "returnDate",
          headerName: "Return Date",
          width: 130,
          editable: canEditDocuments,
        
          renderCell: (params) =>
            params.value ? convertYMDToMDY(params.value) : "",
        
          renderEditCell: (params) => (
            <CustomDatePicker
              size="small"
              className="width_100"
              variant="standard"
              value={params.value || ""}
              hasFocus={params.hasFocus}
              isArrowKeyDisabled={true}
              showOneMonthAlert={true}   // if you want your 30-day alert
              onValueChange={(selectedVal) => {
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: selectedVal
                });
              }}
            />
          )
        },
        { field: "apptDate", headerName: "Appt Date", width: 130 },
        {
          field: "bolPodPopStatus",
          headerName: "BOL/POD",
          width: 160,
          renderCell: (params) => {
            const doc = params.row.docColorStatus?.find(
              (d) => d.label === "BOL" || d.label === "POD"
            );
            return (
              <DocumentStatusCell
                status={doc?.status}
                onDoubleClick={() =>
                  doc?.status === "RED" &&
                  handleOpenUpload(params.row,  doc.label)
                }
                isPermission={canEditDocuments}
              />
            );
          }
        },
        {
          field: "TIR",
          headerName: "TIR",
          width: 120,
          renderCell: (params) => {
            const doc = params.row.docColorStatus?.find(
              (d) => d.label === "TIR"
            );
            return (
              <DocumentStatusCell
                status={doc?.status}
                onDoubleClick={() =>
                  doc?.status === "RED" &&
                  handleOpenUpload(params.row, "TIR")
                }
                 isPermission={canEditDocuments}
              />
            );
          }
        },
    
        {
          field: "Prenote",
          headerName: "Prenote",
          width: 120,
          renderCell: (params) => {
            const doc = params.row.docColorStatus?.find(
              (d) => d.label  === "Prenote"
            );
            return (
              <DocumentStatusCell
                status={doc?.status}
                onDoubleClick={() =>
                  doc?.status === "RED" &&
                  handleOpenUpload(params.row, "Prenote")
                }
                isPermission={canEditDocuments}
              />
            );
          }
        },
    
       
        // { field: "exceptionNote", headerName: "Exception Note", width: 220 },
        {
          field: "exceptionNote",
          headerName: "Exception Note",
          width: 220,
          editable: canEditDocuments,
          renderEditCell: (params) => (
            <TextField
              variant="standard"
              fullWidth
              value={params.value || ""}
              onChange={(e) =>
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: e.target.value
                })
              }
            />
          )
        }
      ];

  /* ---------------- RENDER ---------------- */
  return (
    <TraceWrapper>
      <Box>
        <DocumentAuditFilterSection
          selectedLocationList={selectedLocationList}
          setSelectedLocationList={setSelectedLocationList}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          onApplyFilters={setFilters}
          onClearFilters={() => setFilters({})}
          hasUnsavedRows={hasUnsavedRows}
          isSaving={isSaving}
          handleSave={handleSave}
          handleCancel={handleCancel}
        />

       

        <Box
          sx={{
            height: isExpanded ? "calc(100vh - 250px)" : "calc(100vh - 190px)",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "rgba(11, 67, 135, 1)",
              color: "white"
            },
            "& .edited-row": {
              backgroundColor: "#FFF7E6 !important"
            }
          }}
        >
          <DataGridPro
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              const { page: newPage, pageSize: newPageSize } = model;
          
              setPage(newPage);
              setPageSize(newPageSize);
          
              loadData({
                pageParam: newPage,
                pageSizeParam: newPageSize,
                sortParam: sortModel
              });
            }}
            sortModel={sortModel}
            onSortModelChange={handleSortChange}
            // onSortModelChange={setSortModel}
            disableRowSelectionOnClick
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={
              canEditDocuments ? setRowModesModel : undefined
            }
            processRowUpdate={
              canEditDocuments ? processRowUpdate : (row) => row
            }
            isCellEditable={(params) =>
              canEditDocuments && Boolean(params.colDef.editable)
            }
            getRowClassName={(params) =>
              editedRowIds.has(params.id) ? "edited-row" : ""
            }
            onCellDoubleClick={(params) => {
              if (canEditDocuments && params.colDef.editable) {
                setRowModesModel({
                  [params.id]: { mode: GridRowModes.Edit }
                });
              }
            }}
          />
        </Box>

        {uploadState && (
          <PermissionWrapper category="documents" level="READ_WRITE">
          <UploadDocumentModal
            open
            row={uploadState.row}
            docType={uploadState.docTypeDetails}
            onClose={() => setUploadState(null)}
            onSuccess={() => {
              setUploadState(null);
              loadData();
            }}
          />
          </PermissionWrapper>
        )}
      </Box>
    </TraceWrapper>
  );
}
